import React, { useState } from 'react';
import { Mail, Loader2, CheckCircle, ArrowRight } from 'lucide-react';
import Logo from '../components/Logo';

export default function GrowBoxLanding() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      // Subscribe to Mailchimp newsletter
      const mailchimpResponse = await fetch('/.netlify/functions/subscribe-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          email: email.toLowerCase(),
          tags: ['Website', 'Grow Box Early Access'],
          source: 'Grow Box Coming Soon'
        })
      });

      if (!mailchimpResponse.ok) {
        console.error('Failed to subscribe to newsletter');
        // Continue anyway - don't fail the whole submission
      }

      // Submit to Netlify Forms
      const formData = new FormData();
      formData.append('form-name', 'grow-box-early-access');
      formData.append('email', email);

      const response = await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as any).toString()
      });

      if (!response.ok) {
        throw new Error('Failed to submit form');
      }

      // Store email for future use
      localStorage.setItem('blooma_email', email);
      
      setSuccess(true);
      setEmail('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F1EEE7] to-white flex flex-col">
      {/* Hero Section */}
      <div className="flex-grow flex items-center justify-center p-4">
        <div className="max-w-4xl w-full">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            {/* Left Column - Main Content */}
            <div className="text-center lg:text-left">
              <div className="flex justify-center lg:justify-start mb-8">
                <Logo size="lg" showText={false} variant="full" />
              </div>
              
              {/* Headline */}
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                Want to Garden — But Don't Know Where to Start?
              </h1>
              
              {/* Subheadline */}
              <div className="mb-6">
                <h2 className="text-2xl md:text-3xl font-semibold text-primary-600 mb-2">
                  We've Got You.
                </h2>
                <p className="text-xl text-gray-700">
                  Personalized Garden Grow Boxes. <br></br>
                  
                  Everything you need. Right on time. Zero guesswork.
                </p>
              </div>

              {/* Limited Quantities Notice */}
              <div className="bg-orange-100 border border-orange-300 rounded-lg p-3 mb-6 inline-block">
                <p className="text-orange-800 font-semibold text-sm">
                  ⚡ Limited quantities available!
                </p>
              </div>
            </div>

            {/* Right Column - Email Form */}
            <div className="flex justify-center lg:justify-end">
              <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
                {success ? (
                  <div className="text-center">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
                      <CheckCircle className="h-8 w-8 text-green-600" />
                    </div>
                    <h2 className="text-xl font-semibold mb-2">Well, this is exciting!</h2>
                    <p className="text-gray-600 mb-6">
                      You'll be the first to know when the Fall Blooma Grow Boxes launch. 
                    </p>
                    <a 
                      href="/"
                      className="inline-flex items-center gap-2 text-primary-600 hover:text-primary-700 font-medium"
                    >
                      <span>Return to Homepage</span>
                      <ArrowRight className="h-4 w-4" />
                    </a>
                    
                    {/* Featured Image */}
                    <div className="mt-8 flex justify-center">
                      <img
                        src="https://images.unsplash.com/photo-1523348837708-15d4a09cfac2?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80"
                        alt="Gardening tools and plants"
                        className="w-full max-w-sm md:max-w-md lg:max-w-lg mx-auto rounded-lg shadow-md"
                      />
                    </div>
                  </div>
                ) : (
                  <>
                    <h3 className="text-2xl font-bold mb-2">Get Early Access</h3>
                    <p className="text-gray-600 mb-6">
                      Be the first to know when Fall boxes launch — plus get a bonus planting guide.
                    </p>

                    <form 
                      onSubmit={handleSubmit}
                      name="grow-box-early-access"
                      method="POST"
                      data-netlify="true"
                      className="space-y-4"
                    >
                      {/* Hidden input for Netlify Forms */}
                      <input type="hidden" name="form-name" value="grow-box-early-access" />
                      
                      <div className="relative">
                        <input
                          type="email"
                          name="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder="Your email"
                          className="w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
                          required
                        />
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                      </div>

                      {error && (
                        <p className="text-sm text-red-600">{error}</p>
                      )}

                      <button
                        type="submit"
                        disabled={loading}
                        className="w-full bg-primary-500 text-white py-3 px-4 rounded-lg hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 font-semibold"
                      >
                        {loading ? (
                          <Loader2 className="h-5 w-5 animate-spin" />
                        ) : (
                          <>
                            <span>Notify Me First!</span>
                            <ArrowRight className="h-5 w-5" />
                          </>
                        )}
                      </button>
                      
                      <p className="text-center text-gray-600 mt-4">
                        Let us handle your garden.
                      </p>
                    </form>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Value Bullets Section */}
          <div className="mt-12 max-w-2xl mx-auto">
            <div className="bg-white/80 backdrop-blur-sm rounded-lg shadow-lg p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-primary-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-white text-sm font-bold">✓</span>
                  </div>
                  <p className="text-gray-700">Zone-specific plan based on your zip code</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-primary-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-white text-sm font-bold">✓</span>
                  </div>
                  <p className="text-gray-700">Seeds, soil boosters & instructions included</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-primary-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-white text-sm font-bold">✓</span>
                  </div>
                  <p className="text-gray-700">Kid-friendly and beginner-approved</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-primary-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-white text-sm font-bold">✓</span>
                  </div>
                  <p className="text-gray-700">Delivered just in time to plant</p>
                </div>
              </div>
            </div>
          </div>

          {/* Decorative Images */}
          <div className="absolute top-10 left-10 opacity-20 hidden lg:block">
            <img 
              src="https://images.unsplash.com/photo-1416879595882-3373a0480b5b?w=200&h=200&fit=crop&crop=center" 
              alt="Garden seedlings"
              className="w-24 h-24 rounded-full object-cover"
            />
          </div>
          <div className="absolute bottom-20 right-10 opacity-20 hidden lg:block">
            <img 
              src="https://images.unsplash.com/photo-1585320806297-9794b3e4eeae?w=200&h=200&fit=crop&crop=center" 
              alt="Fresh vegetables"
              className="w-32 h-32 rounded-full object-cover"
            />
          </div>
          <div className="absolute top-1/3 right-20 opacity-15 hidden xl:block">
            <img 
              src="https://images.unsplash.com/photo-1416879595882-3373a0480b5b?w=150&h=150&fit=crop&crop=center" 
              alt="Garden tools"
              className="w-20 h-20 rounded-full object-cover"
            />
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="bg-white py-6 border-t">
        <div className="container mx-auto px-4 text-center">
          <p className="text-sm text-gray-600">
            &copy; {new Date().getFullYear()} Blooma. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
}